import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import {
  UpdateUserRolesFormData,
  updateUserRolesSchema,
} from '@/lib/schemas/update-user-roles';
import useYupValidationResolver from '@/lib/hooks/yup';
import {
  useContentTracking,
  useInteractionTracking,
} from '@/lib/hooks/tracking';
import { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import Button from '@ingka/button';
import { UserInfo } from '@/typings/api';
import ControlledCheckbox from '@/components/forms/controlled-checkbox';

type UpdateUserRolesSiderProps = {
  user: Pick<UserInfo, 'email' | 'roles'>;
  confirm: (data: UpdateUserRolesFormData) => Promise<void>;
} & ModalComponentBaseProps;

const UpdateUserRolesSider: FC<UpdateUserRolesSiderProps> = ({
  handleDismiss,
  user,
  confirm,
  ...rest
}) => {
  // Hooks
  const trackContent = useContentTracking();
  const { t } = useTranslation();
  const trackInteraction = useInteractionTracking();
  const [isLoading, setIsLoading] = useState(false);
  // Form
  const resolver = useYupValidationResolver(updateUserRolesSchema);
  const form = useForm({
    mode: 'all',
    resolver,
    defaultValues: {
      isLocationAdmin: user.roles?.includes('CHECKA Location Admin') || false,
      isStockAdmin: user.roles?.includes('CHECKA Stock Admin') || false,
      isOrderAdmin: user.roles?.includes('CHECKA Order Admin') || false,
      isUserAdmin: user.roles?.includes('CHECKA User Admin') || false,
    },
    criteriaMode: 'all',
  });
  const submitIsEnabled = form.formState.isValid && !isLoading;
  const { handleSubmit } = form;
  // Event handlers
  const handleConfirm = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await confirm(data);
      handleDismiss();
      trackInteraction('update-user-roles-submit', {
        content: 'update-user-roles',
      });
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    trackContent('update-user-roles', { type: 'sider' });
  }, []);

  return (
    <Sheets
      {...rest}
      data-testid="updateUserRolesModal"
      aria-label={t('label.updateRoles')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('label.updateRoles')}
        />
      }
      footer={
        <ModalFooter className="space-x-4">
          <Button
            disabled={!submitIsEnabled}
            type="primary"
            onClick={handleConfirm}
          >
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <ControlledCheckbox<UpdateUserRolesFormData>
            name="isLocationAdmin"
            label="CHECKA Location Admin"
            caption={t('label.rolesDescription.locationAdmin')}
          />

          <ControlledCheckbox<UpdateUserRolesFormData>
            name="isStockAdmin"
            label="CHECKA Stock Admin"
            caption={t('label.rolesDescription.stockAdmin')}
          />

          <ControlledCheckbox<UpdateUserRolesFormData>
            name="isOrderAdmin"
            label="CHECKA Order Admin"
            caption={t('label.rolesDescription.orderAdmin')}
          />

          <ControlledCheckbox<UpdateUserRolesFormData>
            name="isUserAdmin"
            label="CHECKA User Admin"
            caption={t('label.rolesDescription.userAdmin')}
          />
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

export default UpdateUserRolesSider;
